import { ChevronRight, MoreHorizontal } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * Breadcrumb — path indicator with middle-collapse truncation. Pencil JyI9B.
 */
export interface BreadcrumbItem {
  label: React.ReactNode
  href?: string
  onClick?: () => void
}

export interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[]
  /** max items before middle-collapse kicks in */
  maxItems?: number
}

export const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ className, items, maxItems = 4, ...props }, ref) => {
    const shouldCollapse = items.length > maxItems
    const visible: BreadcrumbItem[] = shouldCollapse
      ? [items[0]!, { label: <MoreHorizontal size={14} /> }, ...items.slice(-2)]
      : items

    return (
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        className={cn('flex items-center gap-1 text-caption', className)}
        {...props}
      >
        <ol className="flex items-center gap-1">
          {visible.map((item, i) => {
            const isLast = i === visible.length - 1
            return (
              <li key={i} className="flex items-center gap-1">
                {isLast ? (
                  <span className="text-ia-text font-medium">{item.label}</span>
                ) : item.href || item.onClick ? (
                  <a
                    href={item.href ?? '#'}
                    onClick={(ev) => {
                      // SPA-friendly: when an onClick handler is provided
                      // (router-driven navigation), suppress the default
                      // browser navigation so the route is set by the
                      // handler and not by the <a href> default. Items
                      // with only href and no onClick navigate normally.
                      if (item.onClick) {
                        ev.preventDefault()
                        item.onClick()
                      }
                    }}
                    className="text-ia-text-secondary hover:text-ia-text focus-visible:outline-none focus-visible:underline"
                  >
                    {item.label}
                  </a>
                ) : (
                  <span className="text-ia-text-secondary">{item.label}</span>
                )}
                {!isLast && <ChevronRight size={12} className="text-ia-text-tertiary" />}
              </li>
            )
          })}
        </ol>
      </nav>
    )
  },
)
Breadcrumb.displayName = 'Breadcrumb'
